import { Alert, Button, Collapse, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { every, filter, flatMap, isEmpty, map, size, some, sortBy, uniq, uniqBy } from 'es-toolkit/compat';
import React from 'react';

import { DialogChromeFooter, DialogChromeHeader } from 'components/atoms/Dialog/DialogChrome';
import { FileToDownload } from 'components/atoms/DownloadFilesModal';
import Loader from 'components/Loader';
import { ExportErrorAlert } from 'components/ResultsExport/ExportErrorAlert';
import { ExportScopeField, ExportScopeFields } from 'components/ResultsExport/ExportFormFields';
import { ExportFormShell } from 'components/ResultsExport/ExportFormShell';
import { getScopeOptionLabels } from 'components/ResultsExport/exportScopeLabels';
import { useResultsExportContext } from 'components/ResultsExport/useResultsExportContext';
import { toWorkflowResultsExportContext } from 'components/ResultsExport/workflowResultsExportContext';
import { ResultsMode } from 'interfaces/experimentResults';
import { Study } from 'interfaces/study';
import { buildExportErrorReport, describeExportFailure } from 'services/export/exportErrorReport';
import {
  FormattedCellTableEntry,
  FormattedCellTableError,
  prepareFormattedCellTable,
} from 'services/export/formattedCellTables';
import {
  ExportDismissReason,
  StudyExportOperation,
  useStudyExportController,
} from 'services/export/useStudyExportController';
import { CellTableRunDiscoveryFailure } from 'services/export/workflowCellTableDiscovery';
import {
  createCellTableRunCache,
  getCachedCellTableRuns,
  getCellTableEntriesByScope,
  hasUncachedRuns,
  isCellTableScopeComplete,
  loadCellTableRuns,
  refreshCellTableEntryLinks,
  CellTableRunsLoadProgress,
  KeyedCellTableRunScope,
} from 'services/export/workflowCellTableRuns';
import { describeCellTableArtifact } from 'services/export/cellTableArtifactLabel';
import { getUniquePicks, ResolvedPanel, ResolvedSlide, ResultPick } from 'services/export/workflowResultsSelection';
import { workflowDirectRunsFailedGuidance, workflowResultsCheckingMessage } from 'services/export/workflowExportSource';
import { useWorkflowResultsSelection } from './useWorkflowResultsSelection';
import { WorkflowDirectExportStatus, isWorkflowExportResolvingMessage } from './WorkflowDirectExportStatus';
import { isLatestSlideResult, LatestResultChip, ResultStatusChip } from './WorkflowResultsSlideRow';
import { WorkflowResultsSelection } from './WorkflowResultsSelection';
import { WorkflowRunsErrorAlert } from './WorkflowRunsErrorAlert';
import { downloadBlob } from 'utils/exports';
import { formatBytes } from 'utils/helpers';
import { downloadFilesAsZip, getZipEntryFileNames, ZipDownloadError } from 'utils/zipDownload';

const noop = (): void => undefined;

// The column renaming failed on a readable file, so its stored original can still be offered. An expired link is not
// a renaming failure.
const isRenameFailure = (error: unknown): error is FormattedCellTableError =>
  error instanceof FormattedCellTableError && Boolean(error.code) && error.code !== 'source_unavailable';

const getOriginalFileName = (file: FormattedCellTableEntry['file']) => {
  const [name] = getZipEntryFileNames([{ ...file, url: file.urlForGetRequest }]);
  return `${name.replace(/\.parquet$/, '')}_original.parquet`;
};

const downloadOriginalFile = async (file: FormattedCellTableEntry['file'], signal: AbortSignal) => {
  const response = await fetch(file.urlForGetRequest, { signal });
  if (!response.ok) throw new Error(`HTTP ${response.status}: the original file could not be retrieved.`);
  downloadBlob(await response.blob(), getOriginalFileName(file));
};

interface WorkflowCellTablesExportDialogProps {
  study?: Study;
  onClose: () => void;
  // Downloads the default results immediately, including originals when formatting is unavailable.
  autoDownload?: boolean;
}

type LoadState =
  | { phase: 'loading'; message: string }
  | { phase: 'failed'; errors: string[]; failures: CellTableRunDiscoveryFailure[] }
  | {
      phase: 'ready';
      // The tables read for each results source, by scope key. A slide lists the tables of the sources it chose.
      entriesByScope: Record<string, FormattedCellTableEntry[]>;
      // Runs that could not be read or have no final cell tables; the other runs stay downloadable.
      failures: CellTableRunDiscoveryFailure[];
      emptyRunIds: string[];
      // Files that shared a URL with another listed file and are listed only once.
      collapsedDuplicates: number;
      // Set while runs that were added are still being read; the tables read so far stay listed.
      loadingMessage?: string;
      // The scope set this inventory finished loading. A newer choice is not settled until its own load finishes.
      // Cache completeness is not this signal: a read error or a failed run stays incomplete so it can be retried.
      settledScopeKey?: string;
    };

// One table listed under a slide: the table of one chosen result.
interface TableRow {
  resolved: ResolvedSlide;
  pick: ResultPick;
  entry: FormattedCellTableEntry;
}

const getDownloadedColumns = (entry: FormattedCellTableEntry) =>
  entry.mapping ? map(entry.mapping.columns, (column) => column.outputName ?? column.rawName) : entry.columnNames;

const describeRunFailure = (failure: CellTableRunDiscoveryFailure, labelFor: (runId: string) => string) =>
  failure.runId ? `${labelFor(failure.runId)}: ${failure.message}` : failure.message;

const countLabel = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

const PANEL_CELL_TABLES_RESOLVING = "Resolving this panel's cell tables…";

// A zero ready count while discovery is unfinished reads as "none were found". Report ready tables only after one is ready.
const describeCellTableLoad = (readyCount: number, pendingFiles: number, searchingRuns: number) => {
  if (!pendingFiles && !searchingRuns) return undefined;
  const lead = searchingRuns ? 'Finding the final cell tables…' : 'Resolving cell tables…';
  const details = [
    readyCount > 0 ? `${countLabel(readyCount, 'cell table')} ready to download` : '',
    pendingFiles ? `${countLabel(pendingFiles, 'cell table')} still being resolved` : '',
    searchingRuns ? `${countLabel(searchingRuns, 'run')} still being searched` : '',
  ].filter(Boolean);
  return `${lead} ${details.join('; ')}.`;
};

const describeFailedRuns = (count: number) =>
  count === 1
    ? '1 run could not be read, so its tables are not listed'
    : `${count} runs could not be read, so their tables are not listed`;

const toResultsMode = (pick: ResultPick) =>
  pick.source === 'published'
    ? ResultsMode.Published
    : pick.source === 'approved'
    ? ResultsMode.InternallyApproved
    : ResultsMode.Manual;

const getScopeKey = (studyId: string, pick: ResultPick) => `${studyId}|${toResultsMode(pick)}|${pick.runId ?? ''}`;

// What a panel's listed tables share: one downloaded column set, or several.
const describePanelColumns = (rows: TableRow[]) => {
  const sets = new Set(
    map(
      filter(rows, ({ entry }) => entry.status === 'ready'),
      ({ entry }) => JSON.stringify(sortBy(getDownloadedColumns(entry)))
    )
  );
  if (sets.size === 0) return '';
  if (sets.size === 1) {
    const [first] = filter(rows, ({ entry }) => entry.status === 'ready');
    return `Every listed table in this panel downloads with the same ${
      getDownloadedColumns(first.entry).length
    } columns.`;
  }
  return `${sets.size} different column sets across this panel's listed tables; each table lists its own columns.`;
};

const TableColumns: React.FC<{ entry: FormattedCellTableEntry; expanded: boolean; id: string }> = ({
  entry,
  expanded,
  id,
}) => (
  <Collapse in={expanded} unmountOnExit id={id}>
    <Stack
      component="ul"
      spacing={0.25}
      aria-label="Columns"
      sx={{ m: 0, mt: 0.5, pl: 2, maxHeight: 200, overflowY: 'auto' }}
    >
      {map(getDownloadedColumns(entry), (column, index) => (
        <Typography key={`${column}-${index}`} component="li" variant="caption" color="text.secondary">
          {column}
        </Typography>
      ))}
    </Stack>
  </Collapse>
);

// Opened from Review and publication. Final cell tables for the whole study, downloaded as Parquet with the
// study's panel display names in the column names; the stored files are never changed.
export const WorkflowCellTablesExportDialog: React.FC<WorkflowCellTablesExportDialogProps> = ({
  study,
  onClose,
  autoDownload = false,
}) => {
  const baseContext = useResultsExportContext(study);
  const controller = useWorkflowResultsSelection('cellTables', study, { ignoreStoredChoice: autoDownload });
  const { source, selection, panelsInput, getRunLabel } = controller;
  const dismissDirectExportRef = React.useRef<(reason: ExportDismissReason) => void>(() => undefined);
  const { startExport, exportProgressDialog, isExportRunning, isExportDialogOpen } = useStudyExportController(
    (reason) => {
      if (autoDownload) dismissDirectExportRef.current(reason);
    }
  );
  const availability = baseContext.canDownloadCellsTable
    ? source.availability
    : { disabled: true, note: 'You do not have permission to download cell tables.' };
  // An empty specific-run preference blocks only once panels are known and none of them chose a covering run.
  const panelsKnown = !panelsInput.isLoading && !panelsInput.error;
  const preferenceHasNoRuns = source.selection.source === 'run' && panelsKnown && selection.selectableSlideCount === 0;
  const close = () => {
    controller.close();
    onClose();
  };
  const [unreadRunNote, setUnreadRunNote] = React.useState('');
  const pendingOmission = React.useRef('');
  dismissDirectExportRef.current = (reason) => {
    if (reason === 'handed-off' && pendingOmission.current) {
      setUnreadRunNote(pendingOmission.current);
      pendingOmission.current = '';
      return;
    }
    close();
  };

  const [loadState, setLoadState] = React.useState<LoadState>({
    phase: 'loading',
    message: 'Finding the final cell tables…',
  });
  const [expandedColumns, setExpandedColumns] = React.useState<Set<string>>(new Set());
  const [handoffMessage, setHandoffMessage] = React.useState('');

  const { studyId } = baseContext;
  const formatRunFailure = (failure: CellTableRunDiscoveryFailure) => describeRunFailure(failure, getRunLabel);
  // Without the unpublished-results permission there is no source to choose, a study that does not allow
  // export has nothing to choose either, and neither has one without any results, so an unavailable export ends here.
  const isUnavailableForUser =
    availability.disabled &&
    (!source.canViewUnpublishedResults ||
      !study?.allowExport ||
      !baseContext.canDownloadCellsTable ||
      source.hasNoResults);

  // Each results source the slides chose is read once, however many slides follow it: Published, Approved, or a run.
  // What has been read stays for as long as the dialog is open, so a changed choice reads only what was added.
  const picks = React.useMemo(() => getUniquePicks(selection), [selection]);
  const scopes = React.useMemo(
    (): KeyedCellTableRunScope[] =>
      studyId
        ? map(picks, (pick) => ({
            key: getScopeKey(studyId, pick),
            runId: pick.runId,
            casesParams: toWorkflowResultsExportContext(baseContext, {
              resultsMode: toResultsMode(pick),
              orchestrationId: pick.runId,
            }).casesParamsWithoutPaginationAndSorting,
          }))
        : [],
    [picks, studyId, baseContext]
  );
  const scopeKey = map(scopes, 'key').join(',');
  const needsRun = preferenceHasNoRuns && isEmpty(scopes);
  const isAvailable = !availability.disabled && !needsRun;
  const runCache = React.useRef(createCellTableRunCache());
  const hasPanels = !panelsInput.isLoading && !panelsInput.error;

  React.useEffect(() => {
    if (!isAvailable || !studyId || !hasPanels || isEmpty(scopes)) return undefined;
    const abortController = new AbortController();
    const { signal } = abortController;
    const toReady = (
      progress: Omit<CellTableRunsLoadProgress, 'pendingFiles' | 'searchingRuns'> & { loadingMessage?: string }
    ): LoadState => ({
      phase: 'ready',
      entriesByScope: getCellTableEntriesByScope(runCache.current, scopes),
      failures: progress.failures,
      emptyRunIds: progress.emptyRunIds,
      collapsedDuplicates: progress.collapsedDuplicates,
      loadingMessage: progress.loadingMessage,
      settledScopeKey: progress.loadingMessage ? undefined : scopeKey,
    });
    const publish = (progress: CellTableRunsLoadProgress) => {
      if (signal.aborted) return;
      const { entries: loadedEntries, failures, pendingFiles, searchingRuns } = progress;
      const readyCount = size(filter(loadedEntries, { status: 'ready' }));
      const loadingMessage = describeCellTableLoad(readyCount, pendingFiles, searchingRuns);
      if (!loadingMessage && failures.length === scopes.length && isEmpty(loadedEntries)) {
        setLoadState({ phase: 'failed', errors: map(failures, 'message'), failures });
      } else if (loadingMessage && isEmpty(loadedEntries)) {
        setLoadState({ phase: 'loading', message: loadingMessage });
      } else {
        setLoadState(toReady({ ...progress, loadingMessage }));
      }
    };
    if (hasUncachedRuns(runCache.current, scopes)) {
      const message = 'Finding the final cell tables…';
      const cached = getCachedCellTableRuns(runCache.current, scopes);
      setLoadState(
        cached.hasCachedRun
          ? toReady({ ...cached, failures: [], newEntryIds: [], loadingMessage: message })
          : { phase: 'loading', message }
      );
    }
    setHandoffMessage('');
    (async () => {
      try {
        const inventory = await loadCellTableRuns({
          studyId,
          scopes,
          cache: runCache.current,
          signal,
          onUpdate: publish,
        });
        publish({ ...inventory, pendingFiles: 0, searchingRuns: 0 });
      } catch (error) {
        if (!signal.aborted) {
          setLoadState({
            phase: 'failed',
            errors: [describeExportFailure(error, 'The cell tables could not be loaded.')],
            failures: [],
          });
        }
      }
    })();
    return () => abortController.abort();
    // The inventory belongs to the study and the results sources the slides chose.
  }, [isAvailable, studyId, hasPanels, scopeKey]);

  const entriesByScope = loadState.phase === 'ready' ? loadState.entriesByScope : {};
  const getRows = (resolved: ResolvedSlide): TableRow[] =>
    studyId
      ? flatMap(resolved.picks, (pick) =>
          map(
            filter(
              entriesByScope[getScopeKey(studyId, pick)] ?? [],
              (entry) => entry.file.slideId === resolved.slide.slideId
            ),
            (entry) => ({ resolved, pick, entry })
          )
        )
      : [];
  const allRows = flatMap(selection.panels, (panel) => flatMap(panel.slides, getRows));
  const listedEntries = uniqBy(map(allRows, 'entry'), (entry) => entry.file.id);
  const exportableEntries = filter(listedEntries, { status: 'ready' });
  const unreadableEntries = filter(listedEntries, { status: 'error' });
  const unavailableCount = size(listedEntries) - size(exportableEntries) - size(unreadableEntries);
  // Footer Download includes every selected table. One that cannot be formatted is the stored original.
  const selectedEntries = uniqBy(
    map(
      filter(allRows, ({ resolved }) => resolved.isSelected),
      'entry'
    ),
    (entry) => entry.file.id
  );
  const selectedOriginalCount = size(filter(selectedEntries, (entry) => entry.status !== 'ready'));
  const sourceLabel = (() => {
    const labels = map(picks, (pick) =>
      pick.source === 'published'
        ? 'Published results'
        : pick.source === 'approved'
        ? 'Approved results'
        : getRunLabel(pick.runId ?? '')
    );
    return labels.join('; ') || 'No results chosen';
  })();
  const reportFor = (errors: string[]) =>
    buildExportErrorReport({
      operation: 'Export cell tables (formatted Parquet)',
      studyId,
      labId: baseContext.labId,
      source: sourceLabel,
      errors,
    });
  // Every table keeps its stored file downloadable; one that cannot be formatted is downloaded as that original.
  const getOriginalReasons = (downloadEntries: FormattedCellTableEntry[]) =>
    new Map(
      map(
        filter(downloadEntries, (entry) => entry.status !== 'ready'),
        (entry) => [entry.file.id, entry.reasons.join(' ')] as [string, string]
      )
    );

  const isLoadingMore = loadState.phase === 'ready' && Boolean(loadState.loadingMessage);
  const guidance = !isAvailable
    ? availability.note ?? (needsRun ? 'Select at least one post-processing run.' : '')
    : panelsInput.isLoading
    ? 'Loading panel information across the cohort…'
    : panelsInput.error
    ? 'The panels could not be loaded, so the slides cannot be grouped by panel.'
    : isEmpty(scopes)
    ? 'Select at least one slide with a result to export.'
    : loadState.phase === 'loading'
    ? loadState.message
    : loadState.phase === 'failed'
    ? 'An error stopped the cell tables from loading.'
    : loadState.loadingMessage && isEmpty(selectedEntries)
    ? loadState.loadingMessage
    : isEmpty(listedEntries)
    ? 'There are no final cell tables for this study and result source.'
    : isEmpty(selectedEntries)
    ? 'Select at least one slide with a cell table.'
    : '';

  // `originals` holds the tables whose stored file is downloaded instead of a formatted one, with the reason their
  // formatting failed. A failed attempt offers those tables again as originals, keeping the others formatted.
  const startDownload = (downloadEntries: FormattedCellTableEntry[], originals: Map<string, string> = new Map()) => {
    if (!studyId || isEmpty(downloadEntries)) return;
    setHandoffMessage('');
    const buildOperation = (originalReasons: Map<string, string>): StudyExportOperation => {
      const failures = new Map(originalReasons);
      // A cached link that no longer works is asked for again once; the table keeps the mapping the user reviewed.
      const prepareEntry = async (entry: FormattedCellTableEntry, signal: AbortSignal) => {
        try {
          return await prepareFormattedCellTable({ studyId, entry, signal });
        } catch (error) {
          if (isRenameFailure(error)) failures.set(entry.file.id, error.message);
          if (!(error instanceof FormattedCellTableError) || error.code !== 'source_unavailable') throw error;
          const refreshed = await refreshCellTableEntryLinks({ cache: runCache.current, entry, signal });
          setLoadState((state) =>
            state.phase === 'ready'
              ? {
                  ...state,
                  entriesByScope: Object.fromEntries(
                    map(Object.entries(state.entriesByScope), ([key, listed]) => [
                      key,
                      map(listed, (candidate) => (candidate.file.id === entry.file.id ? refreshed : candidate)),
                    ])
                  ),
                }
              : state
          );
          try {
            return await prepareFormattedCellTable({ studyId, entry: { ...entry, file: refreshed.file }, signal });
          } catch (retryError) {
            if (isRenameFailure(retryError)) failures.set(entry.file.id, retryError.message);
            throw retryError;
          }
        }
      };
      return {
        title: 'Cell tables export',
        canCancel: true,
        closeOnSuccess: true,
        initialStage: 'Preparing the cell tables…',
        getFallback: () => {
          const failed = filter(downloadEntries, ({ file }) => failures.has(file.id));
          const hasNewFailure = some(failed, ({ file }) => !originalReasons.has(file.id));
          if (!hasNewFailure) return undefined;
          const titles = map(failed, ({ file }) => file.title).join(', ');
          const reasons = uniq(map(failed, ({ file }) => `${file.title}: ${failures.get(file.id)}`));
          return {
            label:
              downloadEntries.length === 1
                ? 'Download original'
                : `Download with ${size(failed)} original ${size(failed) === 1 ? 'file' : 'files'}`,
            message:
              downloadEntries.length === 1
                ? `The column names could not be formatted for ${titles}. Please contact Nucleai support and include the error details. You can still download the original file, with its stored column names.`
                : `The column names could not be formatted for ${titles}. Please contact Nucleai support and include the error details. You can still download the ZIP: the other tables stay formatted and ${
                    size(failed) === 1 ? 'this table is' : 'these tables are'
                  } included as the original file, marked "_original" in the file name.`,
            report: reportFor(reasons),
            operation: buildOperation(failures),
          };
        },
        run: async (signal, reportProgress) => {
          if (downloadEntries.length === 1) {
            const [entry] = downloadEntries;
            if (originalReasons.has(entry.file.id)) {
              await downloadOriginalFile(entry.file, signal);
              setHandoffMessage('The original file was handed to your browser. Check your downloads folder.');
              return;
            }
            const blob = await prepareEntry(entry, signal);
            const [fileName] = getZipEntryFileNames([{ ...entry.file, url: entry.file.urlForGetRequest }]);
            downloadBlob(blob, fileName);
            setHandoffMessage('The download was handed to your browser. Check your downloads folder.');
            return;
          }
          const files: FileToDownload[] = map(downloadEntries, (entry) =>
            originalReasons.has(entry.file.id)
              ? {
                  id: entry.file.id,
                  url: entry.file.urlForGetRequest,
                  title: entry.file.title,
                  fileName: getOriginalFileName(entry.file),
                  contentLength: entry.byteLength,
                }
              : {
                  id: entry.file.id,
                  url: entry.file.urlForGetRequest,
                  title: entry.file.title,
                  caseId: entry.file.caseId,
                  slideId: entry.file.slideId,
                  orchestrationId: entry.file.orchestrationId,
                  contentLength: entry.byteLength,
                  prepare: (prepareSignal: AbortSignal) => prepareEntry(entry, prepareSignal),
                }
          );
          try {
            await downloadFilesAsZip({
              files,
              archiveName: `cell_tables_study_${studyId}_formatted.zip`,
              signal,
              onProgress: ({ phase, completedFiles, totalFiles }) =>
                reportProgress({
                  stage:
                    phase === 'fetching'
                      ? `Preparing the cell tables… ${completedFiles} of ${totalFiles}`
                      : phase === 'archiving'
                      ? 'Creating the ZIP archive…'
                      : 'Handing the ZIP to your browser…',
                  current: completedFiles,
                  total: totalFiles,
                }),
            });
          } catch (error) {
            if (error instanceof ZipDownloadError && error.failureReasons.length) {
              throw new Error(`${error.message}. ${uniq(error.failureReasons).join(' ')}`);
            }
            throw error;
          }
          setHandoffMessage('The ZIP was handed to your browser. Check your downloads folder.');
        },
      };
    };
    void startExport(buildOperation(originals));
  };

  const discoveryFailures = loadState.phase === 'ready' || loadState.phase === 'failed' ? loadState.failures : [];
  const failuresFor = (pick: ResultPick) =>
    filter(discoveryFailures, (failure) => (pick.runId ? failure.runId === pick.runId : failure.runId == null));

  const renderSlideTables = (resolved: ResolvedSlide) => {
    const rows = getRows(resolved);
    if (isEmpty(resolved.picks) || !studyId) return null;
    const pickFailures = flatMap(resolved.picks, failuresFor);
    const isScopeDone = every(resolved.picks, (pick) =>
      isCellTableScopeComplete(runCache.current, getScopeKey(studyId, pick))
    );
    const failureNote = !isEmpty(pickFailures) ? (
      <Typography variant="caption" color="error" sx={{ pl: 4 }}>
        {map(pickFailures, formatRunFailure).join(' ')}
      </Typography>
    ) : null;
    if (isEmpty(rows)) {
      if (failureNote) return failureNote;
      return loadState.phase === 'ready' && isScopeDone ? (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 4 }}>
          No cell table is ready for this slide.
        </Typography>
      ) : null;
    }
    return (
      <Stack spacing={0.5}>
        {failureNote}
        <Stack spacing={0.5} sx={{ pl: 4 }} component="ul" aria-label={`Cell tables of ${resolved.slide.label}`}>
          {map(rows, ({ pick, entry }) => {
            const columnsId = `workflow-cell-table-columns-${entry.file.id}`;
            const isExpanded = expandedColumns.has(entry.file.id);
            const result = resolved.slide.results.find(
              (candidate) => candidate.runId === (pick.runId ?? entry.file.orchestrationId)
            );
            const title = pick.runId
              ? getRunLabel(pick.runId)
              : `${pick.source === 'published' ? 'Published results' : 'Approved results'}${
                  entry.file.orchestrationId && result ? ` (${getRunLabel(entry.file.orchestrationId)})` : ''
                }`;
            const artifactName = describeCellTableArtifact(entry.file) ?? 'Cell table';
            const sharesArtifactName =
              rows.length > 1 &&
              rows.filter((row) => (describeCellTableArtifact(row.entry.file) ?? 'Cell table') === artifactName)
                .length > 1;
            // One slide can list more than one file from the same run. The run name alone cannot tell them apart.
            const tableName =
              rows.length > 1
                ? sharesArtifactName
                  ? `${artifactName}, ${countLabel(getDownloadedColumns(entry).length, 'column')}`
                  : artifactName
                : undefined;
            return (
              <Stack key={`${pick.key}-${entry.file.id}`} component="li" sx={{ listStyle: 'none' }}>
                <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                  <Stack spacing={0} sx={{ minWidth: 0 }}>
                    {tableName ? (
                      <Typography variant="body2" component="span">
                        <strong>{tableName}</strong>
                      </Typography>
                    ) : null}
                    <Typography
                      variant={tableName ? 'caption' : 'body2'}
                      color={tableName ? 'text.secondary' : undefined}
                      component="span"
                    >
                      {title}
                    </Typography>
                  </Stack>
                  {result && <ResultStatusChip status={result.status} />}
                  {result && isLatestSlideResult(resolved.slide.results, result.runId) && <LatestResultChip />}
                  {entry.status === 'ready' && (
                    <Typography variant="caption" color="text.secondary">
                      {entry.byteLength ? `${formatBytes(entry.byteLength)} · ` : ''}
                      {countLabel(getDownloadedColumns(entry).length, 'column')}
                    </Typography>
                  )}
                  {entry.status !== 'ready' &&
                    map(entry.reasons, (reason) => (
                      <Typography key={reason} variant="caption" color="error">
                        {reason}
                      </Typography>
                    ))}
                  <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
                    {entry.status === 'ready' && (
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        aria-expanded={isExpanded}
                        aria-controls={columnsId}
                        onClick={() =>
                          setExpandedColumns((previous) => {
                            const next = new Set(previous);
                            if (next.has(entry.file.id)) next.delete(entry.file.id);
                            else next.add(entry.file.id);
                            return next;
                          })
                        }
                      >
                        {isExpanded ? 'Hide columns' : 'Show columns'}
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="text"
                      color="inherit"
                      aria-label={`Download ${resolved.slide.label}${tableName ? `, ${tableName}` : ''} from ${title}`}
                      disabled={isExportRunning}
                      onClick={() => startDownload([entry], getOriginalReasons([entry]))}
                    >
                      Download
                    </Button>
                  </Stack>
                </Stack>
                {entry.status === 'ready' && <TableColumns entry={entry} expanded={isExpanded} id={columnsId} />}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    );
  };

  const isSearchingCellTables = source.isLoadingRuns || loadState.phase === 'loading' || isLoadingMore;
  const slideTablesPending = (resolved: ResolvedSlide) => {
    if (!studyId || !isSearchingCellTables || !isEmpty(getRows(resolved))) return false;
    if (isEmpty(resolved.picks)) return true;
    return !every(resolved.picks, (pick) => isCellTableScopeComplete(runCache.current, getScopeKey(studyId, pick)));
  };
  const renderPanelNote = ({ panel, slides }: ResolvedPanel) => {
    const note = describePanelColumns(flatMap(slides, getRows));
    const pending = slides.some(slideTablesPending);
    if (!pending && !note) return null;
    return (
      <Stack spacing={0.5} key={`${panel.key}-note`}>
        {pending && <Loader inline message={PANEL_CELL_TABLES_RESOLVING} ariaLabel={PANEL_CELL_TABLES_RESOLVING} />}
        {note ? (
          <Typography variant="caption" color="text.secondary">
            {note}
          </Typography>
        ) : null}
      </Stack>
    );
  };

  const isReady = isAvailable && loadState.phase === 'ready';
  // A refresh keeps the previous panels and runs on screen, so the selection is not current until it settles.
  const directRefreshNote = panelsInput.isFetching
    ? 'Loading panel information across the cohort…'
    : source.isRefreshingRuns
    ? workflowResultsCheckingMessage
    : '';
  const directStarted = React.useRef(false);
  React.useEffect(() => {
    if (!autoDownload || directStarted.current || isExportRunning || isExportDialogOpen || directRefreshNote) return;
    if (!isAvailable || !hasPanels || panelsInput.error || isEmpty(scopes) || !studyId) return;
    if (loadState.phase !== 'ready' || loadState.loadingMessage || loadState.settledScopeKey !== scopeKey) return;
    if (isEmpty(selectedEntries)) return;
    directStarted.current = true;
    pendingOmission.current = isEmpty(loadState.failures)
      ? ''
      : `${describeFailedRuns(size(loadState.failures))}. ${map(loadState.failures, formatRunFailure).join(
          ' '
        )} The downloaded files do not include those runs.`;
    startDownload(selectedEntries, getOriginalReasons(selectedEntries));
  }, [
    autoDownload,
    directRefreshNote,
    formatRunFailure,
    hasPanels,
    isAvailable,
    isExportDialogOpen,
    isExportRunning,
    loadState,
    panelsInput.error,
    scopeKey,
    scopes,
    selectedEntries,
    startDownload,
    studyId,
  ]);
  // A failed run list leaves no Approved or run scope. Published scopes need no list and keep `scopes` non-empty.
  const directRunsListFailed =
    !isUnavailableForUser &&
    source.hasRunsError &&
    isEmpty(source.runs) &&
    hasPanels &&
    !panelsInput.isFetching &&
    !source.isRefreshingRuns &&
    isEmpty(scopes);
  const directStatusMessage =
    unreadRunNote ||
    directRefreshNote ||
    (directRunsListFailed ? workflowDirectRunsFailedGuidance : '') ||
    guidance ||
    'Preparing the cell tables export…';
  const showDirectStatus = autoDownload && !isExportDialogOpen;
  // The chooser shows these failures inline. The direct view has no chooser, so it names them where it stops.
  const directFailureErrors =
    loadState.phase === 'failed'
      ? isEmpty(loadState.failures)
        ? loadState.errors
        : map(loadState.failures, formatRunFailure)
      : loadState.phase === 'ready' && !loadState.loadingMessage && isEmpty(selectedEntries)
      ? map(loadState.failures, formatRunFailure)
      : [];
  const directFailureAlert =
    isAvailable && !unreadRunNote && !isEmpty(directFailureErrors) ? (
      <ExportErrorAlert
        title={
          loadState.phase === 'ready'
            ? describeFailedRuns(size(loadState.failures))
            : 'The cell tables could not be loaded'
        }
        errors={directFailureErrors}
        report={reportFor(directFailureErrors)}
      />
    ) : null;

  return (
    <>
      {showDirectStatus ? (
        <WorkflowDirectExportStatus
          title="Export cell tables"
          message={directStatusMessage}
          resolving={!unreadRunNote && isWorkflowExportResolvingMessage(directStatusMessage)}
          onClose={close}
        >
          {directRunsListFailed ? (
            <WorkflowRunsErrorAlert
              kind="cellTables"
              studyId={studyId}
              labId={baseContext.labId}
              error={source.runsError}
            />
          ) : (
            directFailureAlert
          )}
        </WorkflowDirectExportStatus>
      ) : null}
      <Dialog
        open={!autoDownload}
        fullWidth
        maxWidth="xl"
        onClose={close}
        onClick={(event) => event.stopPropagation()}
        aria-labelledby="workflow-cell-tables-export-title"
      >
        <DialogChromeHeader
          titleId="workflow-cell-tables-export-title"
          title="Export cell tables"
          onClose={close}
          closeButtonAriaLabel="Close export"
        />
        {isUnavailableForUser || !study ? (
          <>
            <DialogContent dividers>
              <Typography variant="body2">{availability.note}</Typography>
            </DialogContent>
            <DialogChromeFooter>
              <Button color="inherit" onClick={close}>
                Close
              </Button>
            </DialogChromeFooter>
          </>
        ) : (
          <ExportFormShell
            study={study}
            optionsTitle="Results to export"
            downloadOnly
            scope={
              <ExportScopeFields>
                <ExportScopeField
                  id="workflow-cell-tables-cases"
                  label="Cases"
                  options={[{ value: 'whole', label: getScopeOptionLabels(false).whole }]}
                  value="whole"
                  onChange={noop}
                />
              </ExportScopeFields>
            }
            options={
              <WorkflowResultsSelection
                study={study}
                kind="cellTables"
                controller={controller}
                operation="Export cell tables"
                description="Final cell tables from the Cell typing & marker positivity runs, downloaded as Parquet with panel names in the column headings. The stored files are not changed."
                renderSlideDetail={renderSlideTables}
                renderPanelNote={renderPanelNote}
                getSlideSearchText={(resolved) => map(getRows(resolved), ({ entry }) => entry.file.title).join(' ')}
              >
                {isAvailable && loadState.phase === 'loading' && hasPanels && !isEmpty(scopes) && (
                  <Loader inline message={loadState.message} ariaLabel="Loading cell tables" />
                )}
                {isReady && isLoadingMore && loadState.phase === 'ready' && loadState.loadingMessage && (
                  <Loader inline message={loadState.loadingMessage} ariaLabel="Loading cell tables" />
                )}
                {isAvailable && loadState.phase === 'failed' && (
                  <ExportErrorAlert
                    title="The cell tables could not be loaded"
                    errors={isEmpty(loadState.failures) ? loadState.errors : map(loadState.failures, formatRunFailure)}
                    report={reportFor(
                      isEmpty(loadState.failures) ? loadState.errors : map(loadState.failures, formatRunFailure)
                    )}
                  />
                )}
                {isReady && loadState.phase === 'ready' && !isEmpty(loadState.failures) && (
                  <ExportErrorAlert
                    title={describeFailedRuns(size(loadState.failures))}
                    errors={map(loadState.failures, formatRunFailure)}
                    report={reportFor(map(loadState.failures, formatRunFailure))}
                  />
                )}
                {isReady && !isEmpty(unreadableEntries) && (
                  <ExportErrorAlert
                    title={`${countLabel(size(unreadableEntries), 'cell table')} could not be read`}
                    errors={uniq(map(unreadableEntries, ({ file, reasons }) => `${file.title}: ${reasons.join(' ')}`))}
                    report={reportFor(
                      map(unreadableEntries, ({ file, reasons }) => `${file.title}: ${reasons.join(' ')}`)
                    )}
                  />
                )}
                {isReady && loadState.phase === 'ready' && !isEmpty(loadState.emptyRunIds) && (
                  <Alert severity="info">
                    No final cell tables in {loadState.emptyRunIds.length === 1 ? 'run' : 'runs'}{' '}
                    {map(loadState.emptyRunIds, getRunLabel).join(', ')}.
                  </Alert>
                )}
                {isReady && loadState.phase === 'ready' && loadState.collapsedDuplicates > 0 && (
                  <Alert severity="info">
                    {countLabel(loadState.collapsedDuplicates, 'duplicate cell table')} sharing a file with another
                    listed table {loadState.collapsedDuplicates === 1 ? 'was' : 'were'} listed once.
                  </Alert>
                )}
                {isReady && !isEmpty(listedEntries) && (
                  <Typography variant="body2" color="text.secondary" role="status">
                    {size(selectedEntries) - selectedOriginalCount} of {size(exportableEntries)} exportable cell tables
                    selected
                    {unavailableCount > 0 ? `; ${unavailableCount} cannot be exported with formatted names` : ''}
                    {size(unreadableEntries) > 0
                      ? `; ${size(unreadableEntries)} could not be read because of an error`
                      : ''}
                    {selectedOriginalCount > 0
                      ? `; ${countLabel(selectedOriginalCount, 'original file')} selected`
                      : ''}
                    .
                  </Typography>
                )}
                {handoffMessage && <Alert severity="success">{handoffMessage}</Alert>}
              </WorkflowResultsSelection>
            }
            primaryLabel="Download"
            primaryDisabled={Boolean(guidance) || isExportRunning}
            primaryGuidance={guidance}
            onPrimary={() => startDownload(selectedEntries, getOriginalReasons(selectedEntries))}
            onBack={close}
            backLabel="Close"
          />
        )}
      </Dialog>
      {exportProgressDialog}
    </>
  );
};
